// Local implementation of class-variance-authority to work around v0 runtime issues
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

type ClassProp = ClassValue

type StringToBoolean<T> = T extends "true" | "false" ? boolean : T

type ConfigSchema = Record<string, Record<string, ClassValue>>

type ConfigVariants<T extends ConfigSchema> = {
  [Variant in keyof T]?: StringToBoolean<keyof T[Variant]> | null | undefined
}

type Config<T extends ConfigSchema> = T extends ConfigSchema
  ? {
      variants?: T
      defaultVariants?: ConfigVariants<T>
      compoundVariants?: (ConfigVariants<T> & { class?: ClassValue; className?: ClassValue })[]
    }
  : never

export type VariantProps<T extends (...args: unknown[]) => unknown> = T extends (props?: infer P) => string
  ? Omit<P, "class" | "className">
  : never

export function cva<T extends ConfigSchema>(base?: ClassValue, config?: Config<T>) {
  return (props?: ConfigVariants<T> & { class?: ClassValue; className?: ClassValue }) => {
    if (!config?.variants) {
      return twMerge(clsx(base, props?.class, props?.className))
    }

    const { variants, defaultVariants, compoundVariants } = config

    const getVariantClassNames = Object.keys(variants).map((variant) => {
      const variantProp = props?.[variant as keyof typeof props]
      const defaultVariantProp = defaultVariants?.[variant]

      const variantKey = (variantProp ?? defaultVariantProp) as string | undefined

      if (variantKey) {
        return variants[variant]?.[variantKey]
      }

      return null
    })

    const compoundVariantClassNames = compoundVariants?.reduce<ClassValue[]>(
      (acc, { class: cvClass, className: cvClassName, ...cvConfig }) => {
        const isMatch = Object.entries(cvConfig).every(([cvKey, cvValue]) => {
          const propValue = props?.[cvKey as keyof typeof props] ?? defaultVariants?.[cvKey]
          return propValue === cvValue
        })

        if (isMatch) {
          acc.push(cvClass, cvClassName)
        }

        return acc
      },
      [],
    )

    return twMerge(clsx(base, getVariantClassNames, compoundVariantClassNames, props?.class, props?.className))
  }
}
